import styled from "styled-components";
import { Flex, Button, Heading } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useGameContext } from "../../../context/gameContext/gameContext";
import selectChoice from "../../../assets/selectChoice.wav";
import { chooseBid } from "../../../utils/ai/bidding";
import { isBotSeat } from "../../../utils/ai/botController";

const BiddingOptions = ({
  dealer,
  numPlayers,
  gameMode,
  setTrump,
  setBid,
  initializeBiddingRound,
  redeal,
  setFourSubmitted,
  playersInfo,
  onBotBidTrump,
}) => {
  // numPlayers alone can't tell 4-player Cutthroat apart from 2v2 Teams
  // (both are 4) - chooseBid needs to know which mode this is to pick the
  // right bidding-aggressiveness margin.
  const isCutthroat = gameMode === "Cutthroat";

  const [buttons, setButtons] = useState({
    bid2: false,
    bid3: false,
    bid4: false,
  });

  // Was hardcoded to wrap after seat 4 - for 3-player Cutthroat, bidding
  // would try to hand off to a nonexistent P4 as soon as it reached the
  // dealer's seat, and since P4 has no hand, the bot-turn effect below
  // refuses to act for them, freezing bidding forever. Number(numPlayers)
  // wraps at the actual seat count instead (still 4 for 2v2 Teams and
  // 4-player Cutthroat, so no behavior change there).
  const seatCount = Number(numPlayers);
  const initialTurn = dealer === seatCount ? 1 : dealer + 1;

  const [turnToBid, setTurnToBid] = useState(initialTurn);
  const [bidCount, setBidCount] = useState(0);
  const [winningBid, setWinningBid] = useState(0);
  const [winningBidder, setWinningBidder] = useState(0);
  const [currentBid, setCurrentBid] = useState("");
  const [currentBidder, setCurrentBidder] = useState("");
  const [names, setNames] = useState("");

  // Dealer's "steal" option: whenever someone other than the dealer bids 4,
  // bidding pauses and jumps straight to the dealer, who may either take
  // over that 4 bid themselves or let it stand.
  const [stealPending, setStealPending] = useState(false);
  const [originalFourBidder, setOriginalFourBidder] = useState(null);

  const winningTeam =
    winningBidder === 1 || winningBidder === 3 ? "teamA" : "teamB";

  const {
    updatePlayerBid,
    resetPlayerBids,
    submitWinningBid,
    resetWinningBid,
    setCurrentTurn,
    username,
    botNames,
  } = useGameContext();

  const playerName = (seat) =>
    seat === 1 ? username : botNames[`P${seat}`];

  // bidCount reaching 4 meant "everyone has now bid" - for 3-player
  // Cutthroat only 3 people ever bid, so bidCount would max out at 3 and
  // these checks would never fire, freezing both the all-pass redeal and
  // the normal end-of-bidding resolution.
  useEffect(() => {
    if (winningBid === 0 && bidCount === seatCount) {
      redeal(true);
      setTimeout(() => {
        setWinningBid(0);
        resetWinningBid();
        resetPlayerBids();
        setBidCount(0);
        initializeBiddingRound(true);
      }, 2000);
    } else if (names === "bid4" || bidCount === seatCount) {
      if (names === "bid4") {
        // Whoever's turn just wrapped back to 1 was the one who bid 4 -
        // for 3-player that's seat 3, not a hardcoded 4.
        let fourbidder = turnToBid === 1 ? seatCount : turnToBid - 1;
        updatePlayerBid(fourbidder, names);
        setWinningBid(4);
        setWinningBidder(turnToBid);
        submitWinningBid(turnToBid, winningBid, winningTeam);
        setFourSubmitted(true);
        // setCurrentTurn(turnToBid);
      }

      setBid((prev) => !prev);

      setTimeout(() => {
        updatePlayerBid(turnToBid, names);
        submitWinningBid(winningBidder, winningBid, winningTeam);
        setCurrentTurn(winningBidder);
        resetPlayerBids();
        setWinningBid(0);
        setTrump((prev) => !prev);
        setFourSubmitted(false);
        setBidCount((prev) => 0);
      }, 1500);

      // also make sure that the trump options menu only appears if winningBid.player === player - set currentTurn to player that won the bid
    }
  }, [bidCount, winningBid, names, turnToBid]);

  // Self-contained resolution for a 4 bid once the dealer's steal decision
  // is settled (either outcome) - deliberately doesn't touch `names`, so it
  // never triggers the effect above's own "names === 'bid4'" handling,
  // which assumes a plain (non-steal) bid4 and would resolve the wrong
  // winner here.
  const resolveFourBid = (winnerSeat) => {
    const winnerTeam = winnerSeat === 1 || winnerSeat === 3 ? "teamA" : "teamB";
    updatePlayerBid(winnerSeat, "bid4");
    setWinningBid(4);
    setWinningBidder(winnerSeat);
    setCurrentTurn(winnerSeat);
    submitWinningBid(winnerSeat, 4, winnerTeam);
    setFourSubmitted(true);
    setBid((prev) => !prev);

    setTimeout(() => {
      resetPlayerBids();
      setWinningBid(0);
      setTrump((prev) => !prev);
      setFourSubmitted(false);
      setBidCount(0);
    }, 1500);
  };

  const botActingRef = useRef(false);

  // When it's a bot seat's turn to bid, evaluate the hand and "click" a
  // bid button the same way a human would. During a dealer steal decision,
  // only "steal at 4" vs "pass" are real options, so that's all the bot
  // evaluates (currentWinningBid: 3 restricts chooseBid to just level 4).
  useEffect(() => {
    const seat = `P${turnToBid}`;
    if (!isBotSeat(seat) || botActingRef.current) return;
    if (!stealPending && bidCount >= seatCount) return;
    const hand = playersInfo?.[turnToBid - 1]?.hand;
    if (!hand || hand.length === 0) return;

    botActingRef.current = true;
    const timer = setTimeout(() => {
      const decision = stealPending
        ? chooseBid({ mySeat: seat, myHand: hand, currentWinningBid: 3, numPlayers: seatCount, isCutthroat })
        : chooseBid({ mySeat: seat, myHand: hand, currentWinningBid: winningBid, numPlayers: seatCount, isCutthroat });
      botActingRef.current = false;
      if (decision.trump) onBotBidTrump?.(decision.trump);
      handleFunctions({ target: { name: decision.action } });
    }, 700);

    return () => {
      clearTimeout(timer);
      botActingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnToBid, bidCount, stealPending]);

  function playSound() {
    const audio = new Audio(selectChoice);
    audio.play();
  }

  const submitBid = (e) => {
    const { name } = e.target;
    playSound();

    // The dealer is responding to a steal offer - resolve directly and
    // skip the normal bidding machinery entirely.
    if (stealPending) {
      setStealPending(false);
      resolveFourBid(name === "bid4" ? dealer : originalFourBidder);
      return;
    }

    updateCurrentWinningBid(name);
    setBidCount((prev) => prev + 1);

    if (name === "bid4" || bidCount === seatCount) {
      if (name === "bid4") {
        if (turnToBid === dealer) {
          // Dealer bidding 4 on their own normal turn - nobody left to
          // offer a steal to, so this resolves immediately as before.
          setNames(name);
          setWinningBid(4);
          setWinningBidder(turnToBid);
          setCurrentTurn(turnToBid);
          submitWinningBid(turnToBid, winningBid, winningTeam);
        } else {
          // Someone else bid 4 - give the dealer a chance to steal it
          // before bidding resolves.
          updatePlayerBid(turnToBid, name);
          setOriginalFourBidder(turnToBid);
          setStealPending(true);
          setTurnToBid(dealer);
          return;
        }
      } else {
        updatePlayerBid(turnToBid, name);
        submitWinningBid(winningBidder, winningBid, winningTeam);
        resetPlayerBids();
        setBid((prev) => !prev);
        setWinningBid(0);
        setTrump((prev) => !prev);

        // also make sure that the trump options menu only appears if winningBid.player === player - set currentTurn to player that won the bid
        setBidCount((prev) => 0);
      }
    } else if (name === "bid3") {
      setWinningBid(3);
      setWinningBidder(turnToBid);
      setButtons((prev) => ({
        ...prev,
        bid2: true,
        [name]: !prev.name,
      }));
      updatePlayerBid(turnToBid, name);
    } else if (name === "bid2") {
      setWinningBid(2);
      setWinningBidder(turnToBid);
      setButtons((prev) => ({
        ...prev,
        [name]: !prev.name,
      }));
      updatePlayerBid(turnToBid, name);
    } else {
      updatePlayerBid(turnToBid, name);
    }

    setTurnToBid((prev) => {
      if (prev === seatCount) {
        setTurnToBid(1);
      } else {
        setTurnToBid((prev += 1));
      }
    });
  };

  const updateCurrentWinningBid = (bid) => {
    if (bid === "Pass") {
      return;
    }
    const currentBid = Number(bid.charAt(3));

    if (currentBid > winningBid) {
      setWinningBidder(turnToBid);
    } else return;
  };

  const handleFunctions = (e) => {
    setCurrentBid(e.target.name);
    setCurrentBidder(turnToBid);
    submitBid(e);
  };

  const showBiddingUI = stealPending
    ? turnToBid === 1
    : bidCount < seatCount && turnToBid === 1;

  return (
    <Flex
      direction="column"
      gap="30px"
      alignItems="center"
      px={2}
      position="relative"
      zIndex={3}
    >
      <Heading size={{ base: "md", md: "lg", lg: "xl" }} textAlign="center">
        {stealPending
          ? originalFourBidder === 1
            ? "You bid 4! The dealer can steal it..."
            : turnToBid === 1
            ? `${playerName(originalFourBidder)} bid 4 - steal it?`
            : `${playerName(
                turnToBid
              )} (dealer) can steal ${playerName(
                originalFourBidder
              )}'s bid of 4...`
          : turnToBid === 1
          ? "Your turn to bid"
          : `${playerName(turnToBid)}'s turn to bid`}
      </Heading>

      {showBiddingUI && (
        <Flex justify={"center"} gap={"30px"}>
          <Wrapper>
            {!stealPending && (
              <>
                <Button
                  onClick={handleFunctions}
                  isDisabled={buttons.bid2}
                  sx={{
                    _hover: buttons.bid2 ? {} : { backgroundColor: "gray.200" },
                    _disabled: {
                      cursor: "default",
                      backgroundColor: "#e5e7eb",
                      color: "#93979f",
                      borderColor: "#9c9ea3",
                    },
                  }}
                  name="bid2"
                >
                  2
                </Button>
                <Button
                  onClick={handleFunctions}
                  isDisabled={buttons.bid3}
                  name="bid3"
                  sx={{
                    _hover: buttons.bid3 ? {} : { backgroundColor: "gray.200" },
                    _disabled: {
                      cursor: "default",
                      backgroundColor: "#e5e7eb",
                      color: "#93979f",
                      borderColor: "#9c9ea3",
                    },
                  }}
                >
                  3
                </Button>
              </>
            )}
            <Button
              onClick={handleFunctions}
              isDisabled={buttons.bid4}
              name="bid4"
              sx={{
                _hover: buttons.bid4 ? {} : { backgroundColor: "gray.200" },
                _disabled: {
                  cursor: "default",
                  backgroundColor: "#e5e7eb",
                  color: "#93979f",
                  borderColor: "#9c9ea3",
                },
              }}
            >
              4
            </Button>
            <Button onClick={handleFunctions} name="Pass" id="Pass">
              Pass
            </Button>
          </Wrapper>
        </Flex>
      )}
    </Flex>
  );
};

export default BiddingOptions;

const Wrapper = styled.div`
  Button {
    width: clamp(38px, 10vw, 80px);
    height: clamp(32px, 8vw, 60px);
    font-size: clamp(13px, 3.5vw, 20px);
    border: 1px solid black;
    background-color: white;
  }
  display: flex;
  gap: clamp(6px, 2vw, 20px);
  flex-wrap: wrap;
  justify-content: center;
`;
